import type { NarrativeSonicsReadModel } from "@/components/OmniCine/Timeline/NarrativeSonics.types";
import type { TimelineProposal, TimelineProposalDiff } from "@/compiler/Timeline/timeline.proposals";
import type { TimelineClip, TimelineState } from "@/state/timeline/timeline.types";

export type BuildAiAnnotationContextInput = {
  timelineState: TimelineState;
  proposals?: TimelineProposal[];
  narrativeSummary?: string | null;
  groups?: unknown[];
  selectedClipId?: string | null;
  narrativeSonicsReadModel?: NarrativeSonicsReadModel | null;
  now?: number;
};

export type AiProposalBridgeContext = {
  now: number;
  timelineState: TimelineState;
  proposals: TimelineProposal[];
  narrativeSummary: string;
  groups: unknown[];
  selectedClipId: string | null;
  selectedClip: TimelineClip | null;
  selectedClipLabel: string;
  selectedClipStartSec: number;
  selectedClipEndSec: number;
  anchorTimeSec: number;
  anchorFrame: number;
  anchorTimecode: string;
  fps: number;
  linkedAudio: boolean;
  narrativeSonicsReadModel: NarrativeSonicsReadModel | null;
  existingAnnotationKeys: Set<string>;
};

function toFinite(value: unknown, fallback = 0): number {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function resolveSelectedClipId(state: TimelineState, preferredClipId?: string | null): string | null {
  const requested = String(preferredClipId ?? "").trim();
  if (requested) return requested;

  const selected = String(state.selectedClipId ?? "").trim();
  if (selected) return selected;

  const fallbackIds = (state as any)?.ui?.selectionIds;
  if (Array.isArray(fallbackIds)) {
    const lastId = String(fallbackIds.at(-1) ?? "").trim();
    if (lastId) return lastId;
  }

  return null;
}

function resolveClipTiming(clip: TimelineClip | null): { startSec: number; endSec: number } {
  if (!clip) {
    return { startSec: 0, endSec: 0 };
  }

  const startSec = toFinite(clip.startSec, 0);
  const explicitEndSec = toFinite(clip.endSec, NaN);
  const durSec = toFinite(clip.durSec, 0);
  const endSec = Number.isFinite(explicitEndSec) ? explicitEndSec : startSec + Math.max(0, durSec);

  return {
    startSec: Math.max(0, startSec),
    endSec: Math.max(startSec, endSec),
  };
}

function resolveClipLabel(clip: TimelineClip | null): string {
  return String(clip?.label ?? clip?.mediaId ?? clip?.id ?? "active clip").trim() || "active clip";
}

function inferLinkedAudio(state: TimelineState, clip: TimelineClip | null): boolean {
  if (!clip) return false;
  if (clip.kind === "audio") return true;

  const clipTrackId = String(clip.trackId ?? "").trim();
  const matchingTrack = (state.tracks ?? []).find((track) => String(track.id ?? "").trim() === clipTrackId);
  if (matchingTrack?.kind === "audio") return true;

  return (state.clips ?? []).some((candidate) => {
    if (candidate.id === clip.id) return false;

    const sameWindow =
      toFinite(candidate.startSec, NaN) < toFinite(clip.endSec, NaN) &&
      toFinite(candidate.endSec, NaN) > toFinite(clip.startSec, NaN);

    if (!sameWindow) return false;

    const track = (state.tracks ?? []).find((entry) => entry.id === candidate.trackId);
    return candidate.kind === "audio" || track?.kind === "audio";
  });
}

export function formatAnnotationTimecode(sec: number | null | undefined, fps = 30): string {
  const safeSec = typeof sec === "number" && Number.isFinite(sec) ? Math.max(0, sec) : 0;
  const safeFps = Math.max(1, Math.floor(fps || 30));
  const wholeSeconds = Math.floor(safeSec);
  const hours = Math.floor(wholeSeconds / 3600);
  const minutes = Math.floor((wholeSeconds % 3600) / 60);
  const seconds = wholeSeconds % 60;
  const frames = Math.min(safeFps - 1, Math.floor((safeSec - wholeSeconds) * safeFps + 1e-4));

  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}:${frames.toString().padStart(2, "0")}`;
}

export function annotationFrameIndex(sec: number | null | undefined, fps = 30): number {
  const safeSec = typeof sec === "number" && Number.isFinite(sec) ? Math.max(0, sec) : 0;
  const safeFps = Math.max(1, Math.floor(fps || 30));
  return Math.max(0, Math.floor(safeSec * safeFps));
}

function diffAnchorTimeSec(diff: TimelineProposalDiff): number | null {
  switch (diff.kind) {
    case "marker/add":
      return toFinite(diff.marker?.timeSec, NaN);
    case "marker/move":
      return toFinite(diff.toTimeSec, NaN);
    case "range/add":
      return (toFinite(diff.range?.startSec, NaN) + toFinite(diff.range?.endSec, NaN)) / 2;
    case "range/update":
      return (toFinite(diff.toStartSec, NaN) + toFinite(diff.toEndSec, NaN)) / 2;
    default:
      return null;
  }
}

function resolveAnchorTimeSec(state: TimelineState, clip: TimelineClip | null, proposals: TimelineProposal[]): number {
  for (const proposal of proposals) {
    for (const diff of proposal.diffs ?? []) {
      const candidate = diffAnchorTimeSec(diff);
      if (typeof candidate === "number" && Number.isFinite(candidate)) {
        return clamp(candidate, 0, Math.max(0, state.durationSec));
      }
    }
  }

  const playheadSec = toFinite(state.playheadSec, 0);
  if (clip) {
    const timing = resolveClipTiming(clip);
    return clamp(playheadSec, timing.startSec, timing.endSec || timing.startSec);
  }

  return clamp(playheadSec, 0, Math.max(0, state.durationSec));
}

export function buildAnnotationDedupeKey(input: {
  nodeId?: string | null;
  subtype?: string | null;
  frameIndex?: number | null;
  assetLabel?: string | null;
}): string {
  const nodeId = String(input.nodeId ?? "").trim() || "global";
  const subtype = String(input.subtype ?? "").trim() || "generic";
  const frameIndex = Number.isFinite(input.frameIndex) ? Math.max(0, Math.floor(Number(input.frameIndex))) : 0;
  const assetLabel = String(input.assetLabel ?? "").trim() || "active clip";
  return `${nodeId}|${subtype}|${frameIndex}|${assetLabel.toLowerCase()}`;
}

export function buildAiAnnotationContext(input: BuildAiAnnotationContextInput): AiProposalBridgeContext {
  const timelineState = input.timelineState;
  const proposals = Array.isArray(input.proposals) ? input.proposals.filter(Boolean) : [];
  const selectedClipId = resolveSelectedClipId(timelineState, input.selectedClipId);
  const selectedClip = selectedClipId
    ? (timelineState.clips ?? []).find((clip) => String(clip.id) === selectedClipId) ?? null
    : null;

  const fps = Math.max(1, Math.floor(timelineState.snap?.fps || 30));
  const { startSec, endSec } = resolveClipTiming(selectedClip);
  const anchorTimeSec = resolveAnchorTimeSec(timelineState, selectedClip, proposals);
  const selectedClipLabel = resolveClipLabel(selectedClip);
  const existingAnnotationKeys = new Set(
    (timelineState.annotations ?? []).map((annotation) =>
      buildAnnotationDedupeKey({
        nodeId: annotation.nodeId,
        subtype: annotation.subtype,
        frameIndex: annotation.frameIndex,
        assetLabel: annotation.assetLabel,
      }),
    ),
  );

  return {
    now: Number.isFinite(input.now) ? Number(input.now) : Date.now(),
    timelineState,
    proposals,
    narrativeSummary: String(input.narrativeSummary ?? "").trim(),
    groups: Array.isArray(input.groups) ? input.groups : [],
    selectedClipId,
    selectedClip,
    selectedClipLabel,
    selectedClipStartSec: startSec,
    selectedClipEndSec: endSec,
    anchorTimeSec,
    anchorFrame: annotationFrameIndex(anchorTimeSec, fps),
    anchorTimecode: formatAnnotationTimecode(anchorTimeSec, fps),
    fps,
    linkedAudio: inferLinkedAudio(timelineState, selectedClip),
    narrativeSonicsReadModel: input.narrativeSonicsReadModel ?? null,
    existingAnnotationKeys,
  };
}